import { createStandaloneToast } from '@chakra-ui/react';
import { t } from '@lingui/macro';
import { FormikProps } from 'formik';
import { Dispatch } from 'redux';
import * as Yup from 'yup';

import { closeModal } from '../../../../../..';
import { fetchGetUserProfilesRequest } from '../../../../../../../user-profiles/user-profiles.actions';
import { UserType } from '../../../../../user-profile';
import { fetchUpdateProfileRequest } from './user-profile-modal.actions';
import {
    ProfileFormType, ToastVariants, ToastVariantsType, UserFields
} from './user-profile.types';

export const showToastMessage = (message: string, variant: ToastVariantsType) => {
    const toast = createStandaloneToast();
    const isDuplicate = toast.isActive(message);

    if (!isDuplicate) {
        toast({
            description: message,
            id: message,
            status: variant,
            duration: 3000,
            isClosable: false
        });
    }
};

export const handleCloseModal = (dispatch: Dispatch, isFormDirty = false) => {
    if (isFormDirty) {
        showToastMessage(t`profile.changesNotSaved`, ToastVariants.warning);
    }
    dispatch(closeModal());
};

export const onSubmit = async (
    values: ProfileFormType,
    profileImage: string,
    hasEmailChanged: boolean,
    dispatch: Dispatch) => {
    const {
        title, firstName, lastName, email, currentPassword, newPassword, confirmPassword
    } = values;
    const body = {
        title,
        firstName,
        lastName,
        email,
        currentPassword,
        newPassword,
        confirmationPassword: confirmPassword,
        profileImage
    };
    const response: any = await dispatch(fetchUpdateProfileRequest(JSON.stringify(body)));

    if (response.status >= 400) {
        showToastMessage(response.message, ToastVariants.error);
    } else {
        if (hasEmailChanged) {
            showToastMessage(t`profile.updatedVerificationEmailSent`, ToastVariants.success);
        } else {
            showToastMessage(t`profile.updated`, ToastVariants.success);
        }

        await dispatch(fetchGetUserProfilesRequest());
        handleCloseModal(dispatch);
    }
};

/**
    Password regex - password should have letters and numbers
    and should be 8 to 25 characters.
 */
const PASSWORD = /^(?=.*?[0-9])(?=.*?[A-z])[A-z0-9!@#$%&*]+$/;

export const getValidationSchema = () => {
    return Yup.object().shape({
        [UserFields.firstName]: Yup.string()
            .required(t`profile.firstNameEmpty`),
        [UserFields.lastname]: Yup.string()
            .required(t`profile.lastNameEmpty`),
        [UserFields.email]: Yup.string()
            .required(t`profile.emailMustBeEntered`)
            .email(t`profile.invalidEmailFormat`),
        [UserFields.currentPassword]: Yup.string()
            .min(8, t`profile.passwordLength`)
            .max(25, t`profile.passwordLength`)
            .matches(PASSWORD, t`profile.passwordFormat`),
        [UserFields.newPassword]: Yup.string()
            .min(8, t`profile.passwordLength`)
            .max(25, t`profile.passwordLength`)
            .matches(PASSWORD, t`profile.passwordFormat`),
        [UserFields.confirmPassword]: Yup.string()
            .min(8, t`profile.passwordLength`)
            .max(25, t`profile.passwordLength`)
            .matches(PASSWORD, t`profile.passwordFormat`)
    });
};

export const getInitialValues = (userDetails: UserType | null) => {
    return {
        [UserFields.title]: userDetails?.title,
        [UserFields.firstName]: userDetails?.firstName,
        [UserFields.lastname]: userDetails?.lastName,
        [UserFields.email]: userDetails?.email,
        [UserFields.currentPassword]: '',
        [UserFields.newPassword]: '',
        [UserFields.confirmPassword]: ''
    };
};

export const getTitleValues = () => [
    t`profile.user.title.select`,
    t`profile.user.title.dr`,
    t`profile.user.title.miss`,
    t`profile.user.title.mr`,
    t`profile.user.title.mrs`,
    t`profile.user.title.ms`
];

export const isSubmitButtonDisabled = (
    props: FormikProps<ProfileFormType>,
    selectedProfilePicture: string,
    hasProfileImageChanged: boolean): boolean => {
    const {
        dirty, errors, isSubmitting
    } = props;
    const hasErrors = Object.keys(errors).length > 0;
    const imageValidation = !selectedProfilePicture && !hasProfileImageChanged;

    return imageValidation && (!dirty || isSubmitting || hasErrors);
};

export const onSubmitHandler = async (
    values: ProfileFormType,
    profileImage: string,
    dispatch: Dispatch,
    hasEmailChanged: boolean) => {
    const {
        currentPassword = '', newPassword = '', confirmPassword = ''
    } = values;
    const hasEnteredAnyPassword = currentPassword.length || newPassword.length || confirmPassword.length;
    const shouldEnterAllPasswords =
        !currentPassword.trim().length || !newPassword.trim().length || !confirmPassword.trim().length;
    const passwordsMatch = newPassword === confirmPassword;

    /*
        If either of the password field been entered,
        then all other passwords should be entered as well
    */
    if (hasEnteredAnyPassword) {
        if (shouldEnterAllPasswords) {
            showToastMessage(t`profile.enterAllPasswords`, ToastVariants.error);

            return false;
        } else if (!passwordsMatch) {
            showToastMessage(t`profile.ensurePasswordsMatch`, ToastVariants.error);

            return false;
        }
    }
    await onSubmit(values, profileImage, hasEmailChanged, dispatch);

    return true;
};
